<script setup lang="ts">
import { useAppConfig } from '@/shared/composables/useAppConfig.ts';
import { useMainStore } from '@/core/store/main.ts';
import { Maybe, MaybeNull } from '@/shared/types';
import { checkScrollableParentOf } from '@/shared/utils/check-scrollable-parent.ts';
import { getSafeRef } from '@/shared/utils/template-refs.ts';
import { storeToRefs } from 'pinia';
import {
  computed,
  nextTick,
  onMounted,
  ref,
  Ref,
  watch,
} from 'vue';
import { scrollIntoView } from '@/shared/utils/dom.ts';
import { Capacitor } from '@capacitor/core';
import {
  createAnimation,
  Animation,
  createGesture,
} from '@ionic/vue';
import type { GestureDetail, Gesture } from '@ionic/vue';

const props = withDefaults(defineProps<{
  visible: boolean;
  updateHeight?: boolean;
  background?: string;
  maxHeightPrecent?: number,
}>(), {
  maxHeightPrecent: 100,
  background: undefined,
});

const emit = defineEmits([
  'open',
  'close',
  'height-updated',
]);

const { desktopSlideContainerBorderRadiusPx, desktopSlideContainerHeightMargin } = useAppConfig();

const mainContainer$: Ref<MaybeNull<HTMLElement>> = ref(null);
const staticContent$: Ref<MaybeNull<HTMLElement>> = ref(null);
const scrollableContent$: Ref<MaybeNull<HTMLElement>> = ref(null);
const darkBg$: Ref<MaybeNull<HTMLElement>> = ref(null);

const popupContentHeight = ref(0);

const disableScroll = ref(false);

const popupOpened = ref(false);

let animation: Animation;

let maxTranslateY = 0;

const createPopupAnimation = () => {
  popupContentHeight.value = getContentHeight();

  let translateY = (getContentHeight() * -1);

  if (window.innerWidth > 600) {
    translateY -= desktopSlideContainerHeightMargin.value;
  }

  // Main container animation
  const mainContainerAnim = createAnimation()
    .addElement(getSafeRef(mainContainer$))
    .duration(300)
    .easing('cubic-bezier(0.25, 0.46, 0.45, 0.94)')
    .fromTo('transform', 'translateY(0px)', `translateY(${translateY}px)`);

  // Background fade animation
  const darkBgAnim = createAnimation()
    .addElement(getSafeRef(darkBg$))
    .duration(300)
    .easing('cubic-bezier(0.25, 0.46, 0.45, 0.94)')
    .fromTo('background-color', 'rgba(0,0,0,0)', 'rgba(0,0,0,0.4)');

  // Combine in a parent animation (timeline)
  animation = createAnimation()
    .addAnimation([mainContainerAnim, darkBgAnim])
    .onFinish(() => {
      popupOpened.value = true;
      emit('open');
    });

  updateScrollableContentHeight();

  maxTranslateY = Math.abs(translateY);

  initialTranslateY = maxTranslateY;
};

const openPopup = () => {
  animation.play();
};

const closePopup = () => {
  animation.progressEnd(0, 1)
    .onFinish(() => {
      popupOpened.value = false;
      emit('close');
    });
};

const updatePopupHeight = () => {
  scrollableContent$.value!.style.height = `fit-content`;

  nextTick(() => {
    const newContentHeight = getContentHeight();

    let oldTranslateY = popupContentHeight.value * -1;
    if (window.innerWidth > 600) {
      oldTranslateY -= desktopSlideContainerHeightMargin.value;
    }

    let newTranslateY = newContentHeight * -1;
    if (window.innerWidth > 600) {
      newTranslateY -= desktopSlideContainerHeightMargin.value;
    }

    const updatePopupHeightAnimation = createAnimation()
      .addElement(getSafeRef(mainContainer$))
      .duration(400)
      .easing('cubic-bezier(0.25, 0.46, 0.45, 0.94)') // easeOutQuad
      .fromTo(
        'transform',
        `translateY(${oldTranslateY}px)`,
        `translateY(${newTranslateY}px)`,
      )
      .onFinish(() => {
        emit('height-updated');
        popupContentHeight.value = newContentHeight;

        createPopupAnimation();
      });

    updatePopupHeightAnimation.play();

    maxTranslateY = Math.abs(newTranslateY);

    initialTranslateY = maxTranslateY;

    updateScrollableContentHeight();
  });
};

const { desktopSlideContainerHeight, zoom } = storeToRefs(useMainStore());

const getContentHeight = () => {
  let maxHeight = window.innerHeight;

  if (window.innerWidth > 600) {
    maxHeight = desktopSlideContainerHeight.value;
  }

  // calculate blocks size, without zoom
  const contentHeight = getSafeRef(scrollableContent$).scrollHeight + getSafeRef(staticContent$).scrollHeight;

  // we need to take into account that blocks in ui might have different size then they have in js props because of css zoom,
  // so we need apply zoom to js props, and will get actual size in ui
  const popupHeight = contentHeight * zoom.value;

  return Math.min(popupHeight, maxHeight * (props.maxHeightPrecent / 100));
};

const updateScrollableContentHeight = () => {
  let maxHeight = window.innerHeight;

  if (window.innerWidth > 600) {
    maxHeight = desktopSlideContainerHeight.value;
  }

  // Popup already has real size in ui since it is not zoomed by css, as so as calculated max height.
  const popupHeight = Math.min(mainContainer$.value!.clientHeight, maxHeight * (props.maxHeightPrecent / 100));

  // Calculate available space for scrollable content subtracting ACTUAL (zoomed) staticContent size from ACTUAL popup height.
  const availableSpaceForScrollableContent = popupHeight - (staticContent$.value!.clientHeight * zoom.value);

  // Get size of ACTUAL (zoomed) scrollable block
  const scrollHeight = (scrollableContent$.value!.scrollHeight * zoom.value);

  // Get either scroll height, if it is smaller than max available space, or max available space, AND remove zoom, so css will zoom it by itself
  scrollableContent$.value!.style.height = (Math.min(availableSpaceForScrollableContent, scrollHeight) / zoom.value) + 'px';
};

watch(() => props.visible, (newValue) => {
  if (newValue && !popupOpened.value) {
    createPopupAnimation();

    openPopup();
  } else if (!newValue && popupOpened.value) {
    closePopup();
  }
});

let gesture: Gesture;

let initialTranslateY = 0;

const isSwipingStarted = ref<boolean>(false);

let shouldClose = false;

let prevDeltaY = 0;

/*
  Clamp function to keep number between min and max values
*/
const clamp = (min: number, n: number, max: number) => {
  return Math.max(min, Math.min(n, max));
};

/*
  Get step value between 0 and 1 based on gesture event
*/
const getStep = (event: GestureDetail) => {
  const delta = initialTranslateY + (event.deltaY * -1);

  return clamp(0, delta / maxTranslateY, 1);
};

const onGestureMove = (event: GestureDetail) => {
  if (!isSwipingStarted.value) {
    animation.progressStart();
    isSwipingStarted.value = true;
  }

  if (event.deltaY > 5) {
    disableScroll.value = true;
  }

  shouldClose = event.deltaY > prevDeltaY;

  prevDeltaY = event.deltaY;

  animation.progressStep(getStep(event));
};

const onGestureEnd = (event: GestureDetail) => {
  if (!isSwipingStarted.value) {
    return;
  }

  gesture.enable(false);

  const step = getStep(event);

  const keepOpened = step > 0.8 || !shouldClose;

  animation.progressEnd(keepOpened ? 1 : 0, step)
    .onFinish(() => {
      gesture.enable(true);

      if (!keepOpened) {
        popupOpened.value = false;
        emit('close');
      }
    });

  initialTranslateY = keepOpened ? maxTranslateY : 0;

  isSwipingStarted.value = false;

  disableScroll.value = false;
};

const canGestureStart = (event: GestureDetail) => {
  const canScroll = checkCanScroll(event.event);

  return !canScroll;
};

onMounted(() => {
  if (props.visible && !popupOpened.value) {
    createPopupAnimation();

    openPopup();
  }

  gesture = createGesture({
    el: getSafeRef(mainContainer$),
    threshold: 0,
    gestureName: 'card-drag',
    onMove: (event) => onGestureMove(event),
    canStart: (event) => canGestureStart(event),
    onEnd: (event) => onGestureEnd(event),
  });

  gesture.enable(true);
});

watch(() => props.updateHeight, (newValue) => {
  if (newValue && props.visible) {
    updatePopupHeight();
  }
});

const checkCanScroll = (e: UIEvent) => {
  return checkScrollableParentOf(
    e.target as HTMLElement,
    (node: Element) => node.classList.contains('bottom-sheet'),
  );
};

const scrollTo = (
  element: HTMLElement,
  scrollOptions: Maybe<ScrollIntoViewOptions> = {
    block: 'start',
  },
) => {
  scrollIntoView(
    element,
    getSafeRef(scrollableContent$),
    scrollOptions,
    zoom.value,
  );
};

const { desktopSlideContainerWidth } = storeToRefs(useMainStore());

const desktopSlideContainerWidthPx = computed(() => desktopSlideContainerWidth.value + 'px');

const DEFAULT_BACKGROUND_COLOR = 'rgba(18, 18, 18, 1)';

const backgroundColor = computed(() => props.background || DEFAULT_BACKGROUND_COLOR);

defineExpose({
  closePopup,
  updatePopupHeight,
  scrollTo,
});

// close popup by click outside popup
const closePopupIfFullyOpen = () => {
  // If the popup opening animation has fully completed, we can close it
  if (props.visible) {
    closePopup();
  }
};

const { keyboardVisible } = storeToRefs(useMainStore());

</script>

<template>
  <Teleport to="body">
    <div :class="['bottom-sheet-wrapper', { visible }]">
      <div
        ref="mainContainer$"
        class="bottom-sheet"
        :class="{
          'keyboard-visible': keyboardVisible && props.visible,
        }"
      >
        <div class="blur-circle" />
        <div class="popup-controller remove-blue-highlight">
          <div class="controller-line" />
        </div>
        <div
          ref="staticContent$"
          class="static-content"
        >
          <slot name="static-content" />
        </div>
        <div
          ref="scrollableContent$"
          class="content remove-scrollbar"
          :class="{
            'disable-scroll': disableScroll,
            'disable-pointer-events': isSwipingStarted && !Capacitor.isNativePlatform(),
          }"
        >
          <slot name="scrollable-content" />
        </div>
      </div>
      <div
        v-show="props.visible"
        ref="darkBg$"
        class="dark-bg"
        @click="closePopupIfFullyOpen"
      />
    </div>
  </Teleport>
</template>

<style scoped>
  .bottom-sheet-wrapper {
    position: fixed;
    top: 100vh;
    left: 0;
    right: 0;
    height: 100vh; /* or 100% */
    z-index: 500;
  }

  .bottom-sheet {
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
    overflow: hidden;
    width: 100%;
    max-height: 100%;
    height: auto;
    z-index: 500;
    display: grid;
    grid-template-rows: max-content 1fr;
    background: v-bind('backgroundColor');
    position: relative;

    will-change: transform;
  }

  .blur-circle {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 175px;
    height: 165px;
    border-radius: 50%;
    background-color: rgba(133, 58, 255, 1);
    z-index: 1;
    filter: blur(110px);
  }

  .dark-bg {
    position: absolute;
    top: -100%;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 499;
    background-color: rgba(0,0,0,0);
  }

  .popup-controller {
    padding: 8px;
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    z-index: 100;
    cursor: pointer;
  }

  .controller-line {
    width: 88px;
    height: 5px;
    border-radius: 5px;
    background-color: rgba(255, 255, 255, 0.2);
  }

  .content {
    overflow-y: auto;
    overscroll-behavior: none;

    @media all and (min-width: 601px) {
      zoom: var(--zoom-for-desktop);
    }
  }

  .disable-scroll {
    overflow: hidden;
  }

  .disable-pointer-events {
    pointer-events: none;
  }

  .static-content {
    padding-top: 24px;
    z-index: 2;
    position: relative;

    @media all and (min-width: 601px) {
      zoom: var(--zoom-for-desktop);
    }
  }

  @media all and (min-width: 601px) {
    .bottom-sheet {
      width: v-bind('desktopSlideContainerWidthPx');
      margin: auto;
      /* margin-top: 36px; */
      max-width: calc(100vw - 200px);
      border-bottom-left-radius: v-bind('desktopSlideContainerBorderRadiusPx');
      border-bottom-right-radius: v-bind('desktopSlideContainerBorderRadiusPx');
    }
  }

  .keyboard-visible {
    bottom: var(--ion-keyboard-height);
  }
</style>
